import { h, Component } from 'preact';
import util from 'preact-util';
import { raceLocalSuffix } from '../../lib/raceTime';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import localUtil from '../../lib/util';
import { withoutUnpaidReservations } from '../../lib/raceSignup';

const fieldSorter = (fields) => (a, b) => fields.map(o => {
    let dir = 1;
    if (o[0] === '-') {
        dir = -1;
        o = o.substring(1);
    }
    return a[o] > b[o] ? dir : a[o] < b[o] ? -(dir) : 0;
}).reduce((p, n) => p ? p : n, 0);

@observer
class RaceResultsPrint extends Component {
    render() {
        const { raceStore, raceClassStore, raceClassContestantStore } = this.props.stores;
        const { race } = raceStore;
        const compactWithSuffix = ms => `${util.isoDateCompact(ms)}${raceLocalSuffix(ms, race, 'isoDateCompact')}`;
        const { raceClasses } = raceClassStore;
        // Unpaid forced-payment reservations are not public signups yet.
        const raceClassContestants = withoutUnpaidReservations(raceClassContestantStore.raceClassContestants);

        const finalRaceClasses = raceClasses.filter(rc => !rc.title.includes('\u26a0\ufe0f'));

        const printStyles = `
            @media print {
                body * {
                    visibility: hidden;
                }
                .race-results-print, .race-results-print * {
                    visibility: visible;
                }
                .race-results-print {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                }
                @page {
                    size: A4 portrait;
                    margin: 10mm;
                }
            }
            .race-results-print table {
                border-collapse: collapse;
                width: 100%;
            }
            .race-results-print th, .race-results-print td {
                border: 1px solid #ccc;
                padding: 2px 4px;
                font-size: 9px;
            }
            .race-results-print th {
                background: #f0f0f0;
                font-weight: bold;
            }
            .race-results-print tr:nth-child(even) {
                background: #f9f9f9;
            }
            .race-results-print .cp-cell {
                text-align: center;
                width: 35px;
                min-width: 35px;
            }
            .race-results-print .cp-in {
                background: #0d6efd;
                color: white;
            }
            .race-results-print .cp-current {
                background: #198754;
                color: white;
            }
            .race-results-print .cp-finish {
                background: #198754;
                color: white;
            }
            .race-results-print .cp-scratched {
                background: #dc3545;
                color: white;
            }
            .race-results-print .contestant-cell {
                text-align: left;
            }
            .race-results-print .time-cell {
                text-align: right;
                width: 70px;
                font-weight: bold;
            }
            .race-results-print .uttid {
                color: #666;
                font-size: 8px;
            }
            .race-results-print .scratched {
                text-decoration: line-through;
                color: #999;
            }
            .race-results-print .flag {
                display: inline-block;
                width: 16px;
                height: 11px;
                margin-right: 4px;
                vertical-align: middle;
            }
        `;

        return (
            <div class='race-results-print' style='font-family: Arial, sans-serif; color: #000; background: #fff; padding: 10px;'>
                <style>{printStyles}</style>

                <div style='text-align: center; margin-bottom: 15px;'>
                    <h1 style='font-size: 18px; margin: 0 0 5px 0;'>{race.title}</h1>
                    <div style='font-size: 10px; color: #666;'>
                        <Text id='race.print-date'>Utskriftsdato</Text>: {util.isoDate(new Date(), false, false, true)}
                    </div>
                </div>

                {finalRaceClasses.map((raceClass, classIdx) => {
                    const classContestants = raceClassContestants.filter(rcc => rcc.raceClass === raceClass.id);
                    if (classContestants.length === 0) {
                        return null;
                    }

                    const sortedCheckpoints = raceClass.checkpoints
                        ? raceClass.checkpoints.sort(fieldSorter(['sortOrder', '-name']))
                        : [];

                    let prevContestant = null;
                    let currentPosition = 0;

                    return (
                        <div style={classIdx > 0 ? 'margin-top: 20px; page-break-before: auto;' : ''}>
                            <h2 style='font-size: 12px; margin: 0 0 8px 0; padding: 5px; background: #ffc107; color: #000;'>
                                {race.title}: {raceClass.title}
                            </h2>

                            <table>
                                <thead>
                                    <tr>
                                        {sortedCheckpoints.map((cp, idx) => {
                                            if (cp.sortOrder === 1) {
                                                return (
                                                    <th class='cp-cell'>
                                                        <Text id='race.result-start'>Start</Text>
                                                    </th>
                                                );
                                            }
                                            if (cp.sortOrder === sortedCheckpoints.length) {
                                                return (
                                                    <th class='cp-cell'>
                                                        <Text id='race.result-finish'>Mål</Text>
                                                    </th>
                                                );
                                            }
                                            return (
                                                <th class='cp-cell'>
                                                    {raceClass.sortOrderResults && raceClass.doNotCountRestTime
                                                        ? cp.name || `CP${cp.sortOrder}`
                                                        : <><i class='fa-solid fa-campground' style='font-size: 8px;' /></>
                                                    }
                                                </th>
                                            );
                                        })}
                                        <th style='text-align: left;'><Text id='race.print-contestant'>Deltaker</Text></th>
                                        {!raceClass.doNotCountRestTime && (
                                            <th style='text-align: center; width: 80px;'><Text id='race.firsttimeout'>Uttid</Text></th>
                                        )}
                                        <th class='time-cell'><Text id='race.print-time'>Tid</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {classContestants
                                        .filter(e => e.bib > 0)
                                        .sort(fieldSorter(['hasScratched', '-lastCheckpoint', '-lastCheckpointLeft', (raceClass.sortOrderResults || 'lastTimestamp'), 'bib', '-name']))
                                        .map((rcc, posIdx) => {
                                            const { bib, firstname, lastname, country, startTime, timeused, raceTimeused, totalRaceTime } = rcc;
                                            const cpLength = rcc.lastCheckpoint > 0 ? rcc.lastCheckpoint : 1;

                                            let firstTimeOut = null;
                                            let totalActualRestMinutes = 0;
                                            let totalRequiredRestMinutes = 0;
                                            let hasTakenOptionalRest = false;

                                            const prevRcc = { ...prevContestant };
                                            prevContestant = rcc;

                                            // Calculate display time
                                            const displayTime = raceClass.sortOrderResults && raceClass.doNotCountRestTime
                                                ? raceTimeused
                                                : timeused;

                                            return (
                                                <tr>
                                                    {sortedCheckpoints.slice(0, cpLength).map((cp, idx) => {
                                                        const _prevCp = sortedCheckpoints[idx - 1];
                                                        const prevRccCp = rcc.checkpoints?.find(e => e.checkpoint === _prevCp?.md5) || {};
                                                        const prevHasScratched = prevRccCp.timstampScratch;
                                                        const rccCp = rcc.checkpoints?.find(e => e.checkpoint === cp.md5) || {};

                                                        if (rccCp.optionalRest) {
                                                            hasTakenOptionalRest = rccCp.optionalRest;
                                                        }

                                                        const hasLeftCp = rccCp.timestampOut;
                                                        const hasScratched = rccCp.timstampScratch;
                                                        const isInCp = rccCp.timestampIn && !rccCp.timestampOut;
                                                        const isFinished = sortedCheckpoints.length === idx + 1;
                                                        const isTimeEq = cp.timeEqualization == 1;
                                                        const isPrologue = cp.isPrologue == 1;
                                                        const isStartline = cp.isStartLine == 1;
                                                        const isLastCp = cp.sortOrder === raceClass.checkpoints.length - 1;
                                                        const timeInCp = !isFinished && rccCp.timestampIn && rccCp.timestampOut ? util.dateDiff(rccCp.timestampIn, rccCp.timestampOut) : 0;

                                                        // Calculate position for finish
                                                        if (isFinished && rccCp.positionInToCheckpoint) {
                                                            const prevRccCpFinish = prevRcc.checkpoints?.find(e => e.checkpoint === cp.md5) || {};
                                                            const timeIsEqualToPrev = prevRccCpFinish.timestampIn === rccCp.timestampIn;
                                                            if (!timeIsEqualToPrev) {
                                                                currentPosition = posIdx + 1;
                                                            }
                                                        }

                                                        // Calculate uttid (time out)
                                                        let remainingRestMinutes;
                                                        if (!isPrologue && !isStartline) {
                                                            totalRequiredRestMinutes += cp.requiredRestMinutes ? parseInt(cp.requiredRestMinutes, 10) : 0;
                                                            const totRequiredRestForContestant = totalRequiredRestMinutes + (rcc.timeEqualization?.minutes || 0);
                                                            if (!cp.notPartOfTotalRest) {
                                                                totalActualRestMinutes += timeInCp?.minutes || 0;
                                                            }

                                                            if (cp.useRemainingRest && !hasScratched && isInCp) {
                                                                // useRemainingRest: Calculate remaining mandatory rest at this checkpoint
                                                                remainingRestMinutes = totRequiredRestForContestant - totalActualRestMinutes;
                                                                if (remainingRestMinutes < 0) {
                                                                    remainingRestMinutes = 0;
                                                                }
                                                                firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (remainingRestMinutes * 60 * 1000));
                                                            } else if (isLastCp && !hasScratched && isInCp) {
                                                                // Last checkpoint without useRemainingRest: use requiredRestMinutes
                                                                remainingRestMinutes = cp.requiredRestMinutes;
                                                                if (remainingRestMinutes < 0) {
                                                                    remainingRestMinutes = 0;
                                                                }
                                                                firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (remainingRestMinutes * 60 * 1000));
                                                            } else if (!hasScratched && isInCp) {
                                                                if (cp.requiredRestMinutes > 0) {
                                                                    firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (cp.requiredRestMinutes * 60 * 1000));
                                                                }
                                                                if (isTimeEq) {
                                                                    firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (cp.requiredRestMinutes * 60 * 1000) + (rcc.timeEqualization?.minutes * 60 * 1000));
                                                                }
                                                                if (rccCp.optionalRest) {
                                                                    if (cp.requiredRestMinutesOptional > 0) {
                                                                        firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (cp.requiredRestMinutesOptional * 60 * 1000));
                                                                    }
                                                                    if (cp.timeEqualizationOptional) {
                                                                        firstTimeOut = compactWithSuffix(new Date(rccCp.timestampIn).getTime() + (cp.requiredRestMinutesOptional * 60 * 1000) + (rcc.timeEqualization?.minutes * 60 * 1000));
                                                                    }
                                                                }
                                                            }
                                                        }

                                                        if (prevHasScratched) {
                                                            return null;
                                                        }

                                                        // Start checkpoint - show start time
                                                        if (cp.sortOrder === 1) {
                                                            return (
                                                                <td class='cp-cell'>
                                                                    {util.isoTime(startTime)}
                                                                </td>
                                                            );
                                                        }

                                                        // Scratched at this checkpoint
                                                        if (!hasLeftCp && hasScratched) {
                                                            return (
                                                                <td class='cp-cell cp-scratched'>
                                                                    {rccCp.positionInToCheckpoint ? `${rccCp.positionInToCheckpoint} ` : ''}X
                                                                </td>
                                                            );
                                                        }

                                                        // Finished
                                                        if (isFinished) {
                                                            return (
                                                                <td class={`cp-cell ${rccCp.positionInToCheckpoint ? 'cp-finish' : ''}`}>
                                                                    {raceClass.sortOrderResults && raceClass.doNotCountRestTime
                                                                        ? (rccCp.positionTimeusedOnStage || '')
                                                                        : (rccCp.positionInToCheckpoint || '')
                                                                    }
                                                                </td>
                                                            );
                                                        }

                                                        // Regular checkpoint
                                                        return (
                                                            <td class={`cp-cell ${rccCp.positionInToCheckpoint ? (isInCp ? 'cp-current' : 'cp-in') : ''}`}>
                                                                {raceClass.sortOrderResults && raceClass.doNotCountRestTime
                                                                    ? (rccCp.positionTimeusedOnStage || '')
                                                                    : (rccCp.positionInToCheckpoint || '')
                                                                }
                                                                {!!(hasLeftCp && !isInCp && cp.sortOrder === rcc.lastCheckpointLeft) && ' >>'}
                                                            </td>
                                                        );
                                                    })}

                                                    {/* Empty cells for remaining checkpoints */}
                                                    {sortedCheckpoints.slice(cpLength).map(() => (
                                                        <td class='cp-cell'></td>
                                                    ))}

                                                    {/* Contestant info */}
                                                    <td class='contestant-cell'>
                                                        <span style={`
                                                            display: inline-block;
                                                            background: ${raceClass.bibColor || '#333'};
                                                            color: white;
                                                            padding: 1px 4px;
                                                            border-radius: 3px;
                                                            margin-right: 4px;
                                                            min-width: 25px;
                                                            text-align: center;
                                                            font-size: 8px;
                                                        `}>{bib}</span>
                                                        {localUtil.getFlagSvg(country, 12, false, '')}
                                                        <span class={rcc.hasScratched ? 'scratched' : ''} style='margin-left: 4px;'>
                                                            {util.ucfirst(firstname)} {util.ucfirst(lastname)}
                                                        </span>
                                                        {!!rcc.rnb && <span style='font-size: 7px; color: #666; margin-left: 4px;'>RNB1</span>}
                                                        {!!(rcc.isDNS || rcc.hasDns) && <span style='color: #dc3545; margin-left: 4px;'>DNS</span>}
                                                        {!!rcc.hasWarning && <span style='color: #ffc107; margin-left: 4px;'>!</span>}
                                                        {!!rcc.hasTimepenalty && <span style='color: #ffc107; margin-left: 4px;'>T</span>}
                                                        {!!rcc.isDisqualified && <span style='color: #dc3545; margin-left: 4px;'>DQ</span>}
                                                    </td>

                                                    {/* UTTID column - Critical for print version */}
                                                    {!raceClass.doNotCountRestTime && (
                                                        <td style='text-align: center; font-size: 8px;'>
                                                            {firstTimeOut || ''}
                                                        </td>
                                                    )}

                                                    {/* Time */}
                                                    <td class='time-cell'>
                                                        {rcc.hasScratched ? 'DNF' : (displayTime > 0 ? util.secToHms(displayTime) : '')}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    );
                })}

                <div style='margin-top: 15px; font-size: 8px; color: #666; text-align: center;'>
                    <Text id='race.manual-result-notice'>Resultater registreres manuelt av løpene, kan være forsinket og inneholde feil.</Text>
                </div>
            </div>
        );
    }
}

export default RaceResultsPrint;
